import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import { useState } from 'react';
import { workflowsApi } from '~/api/def/servers/defaultServer';
import { Workflow } from '~/atoms/schemas/workflowSchemas';
import { type TaskEntry } from '~/lib/workflows/useWorkflows';
import { TasksList } from './TasksList';
import { WorkflowGraph } from './WorkflowGraph';

export type WorkflowDetailsPanelProps = {
  selectedWorkflow: Workflow | null;
  taskEntries: TaskEntry[];
  isLoading: boolean;
};

export const WorkflowDetailsPanel = ({
  selectedWorkflow,
  taskEntries,
}: WorkflowDetailsPanelProps) => {
  const [viewMode, setViewMode] = useState<'list' | 'graph'>('list');
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  const viewOptions = [
    { label: 'List', value: 'list', icon: 'pi pi-list' },
    { label: 'Graph', value: 'graph', icon: 'pi pi-chart-line' },
  ];

  const markCompletedMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      const token = await getToken();
      const response = await fetch(
        `${workflowsApi.url}/workflows/${workflowId}/status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: 'completed' }),
        },
      );
      if (!response.ok) throw new Error('Failed to update workflow');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      if (selectedWorkflow?.workflow_id) {
        queryClient.invalidateQueries({
          queryKey: ['workflow', selectedWorkflow.workflow_id],
        });
      }
    },
  });

  return (
    <div
      className="p-3 h-full flex flex-column"
      style={{
        borderRadius: '8px',
        backgroundColor: '#3E3E3E',
      }}
    >
      <div className="border-bottom-1 pb-2" style={{ borderColor: '#333' }}>
        <div className="flex justify-content-between align-items-center mb-2">
          <h3 className="mt-0 mb-2" style={{ color: 'white' }}>
            {selectedWorkflow ? (
              <div className="flex align-items-center">
                <span
                  className="border-round mr-2 px-2 py-1 text-xs font-bold"
                  style={{
                    backgroundColor:
                      selectedWorkflow.status === 'completed'
                        ? '#4caf50'
                        : selectedWorkflow.status === 'running'
                          ? '#2196f3'
                          : selectedWorkflow.status === 'failed'
                            ? '#f44336'
                            : '#ff9800',
                    color: 'white',
                  }}
                >
                  {selectedWorkflow.status?.toUpperCase() || 'PENDING'}
                </span>
                <span>Workflow Details</span>
              </div>
            ) : (
              'Select a workflow to view details'
            )}
          </h3>
          {selectedWorkflow && (
            <div className="flex align-items-center gap-2">
              {selectedWorkflow.status !== 'completed' && (
                <Button
                  label="Mark as Completed"
                  disabled={markCompletedMutation.status === 'pending'}
                  loading={markCompletedMutation.isPending}
                  onClick={() =>
                    markCompletedMutation.mutate(selectedWorkflow.workflow_id)
                  }
                  severity="success"
                  size="small"
                  className="p-button-sm"
                  style={{
                    backgroundColor: '#4CAF4F',
                    color: '#fff',
                    border: '1px solid #4CAF4F',
                    minWidth: 0,
                  }}
                />
              )}
              <SelectButton
                value={viewMode}
                onChange={(e) => setViewMode(e.value)}
                options={viewOptions}
                pt={{
                  button: {
                    style: {
                      backgroundColor: '#2a2a2a',
                      color: 'white',
                      border: '1px solid #444',
                    },
                  },
                }}
                className="p-button-sm"
              />
            </div>
          )}
        </div>
        {selectedWorkflow && (
          <p className="text-sm text-color-secondary m-0">
            {`Showing ${
              Object.keys(selectedWorkflow.task_states).length
            } tasks for this workflow`}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-auto mt-3">
        {viewMode === 'graph' ? (
          <WorkflowGraph workflow={selectedWorkflow} />
        ) : taskEntries.length > 0 ? (
          <TasksList tasks={taskEntries} />
        ) : (
          <div className="p-4 text-center flex align-items-center justify-content-center h-full">
            <div className="text-color-secondary">No tasks available</div>
          </div>
        )}
      </div>
    </div>
  );
};
